import { useParams, Link, Navigate } from 'react-router-dom'
import { useMemo, useCallback, useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useLesson, useLessonProgress, useCourseNavigation } from '@/lib/hooks/use-lessons'
import { useProfile } from '@/lib/hooks/use-profile'
import { useRole } from '@/hooks/use-role'
import { useFreelancerProfile } from '@/lib/hooks/use-freelancer-profile'
import { useUnifiedCourses } from '@/lib/hooks/use-unified-courses'
import { usePortfolioProjects } from '@/lib/hooks/use-portfolio-projects'
import { useWorkExperience } from '@/lib/hooks/use-work-experience'
import { useProfileCompletion } from '@/lib/hooks/use-profile-completion'
import { VimeoPlayer } from '@/components/VimeoPlayer'
import { AnimatedLoader } from '@/components/ui/animated-loader'
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  FileText,
  HelpCircle,
  PlayCircle,
  CheckCircle2,
  Clock,
  GraduationCap
} from 'lucide-react'
import { emailService } from '@/lib/api/services/email.service'

export default function LessonPage() {
  const { lessonId } = useParams<{ lessonId: string }>()
  const { user } = useUser()
  const { profile } = useProfile()
  const { freelancerProfileId } = useRole()
  const { lesson, isLoading: lessonLoading, error } = useLesson(lessonId!)
  const { updateProgress, markComplete, isUpdating } = useLessonProgress(lessonId!)
  
  // Get profile completion data for email sending
  const { freelancerProfile } = useFreelancerProfile(freelancerProfileId, { enableMutations: false })
  const { courses } = useUnifiedCourses()
  const { projects: portfolioProjects } = usePortfolioProjects(freelancerProfileId || '')
  const { experiences: workExperiences } = useWorkExperience(freelancerProfileId || '')
  
  // Calculate course progress
  const enrolledCourses = courses?.filter((c) => c.enrollment) || []
  const completedCourses = enrolledCourses.filter(
    (c) => c.enrollment?.status === "completed"
  ).length
  const totalCourses = courses?.length || 0
  
  const courseProgress = {
    totalCourses,
    completedCourses,
    allCompleted: completedCourses >= totalCourses && totalCourses > 0,
  }
  
  // Calculate profile completion
  const profileCompletion = useProfileCompletion(
    freelancerProfile,
    courseProgress,
    portfolioProjects?.length || 0,
    workExperiences?.length || 0
  )
  
  // Send email when profile reaches 100% completion (only once)
  useEffect(() => {
    if (!user?.id || !profileCompletion.isComplete || !freelancerProfile?.email) return
    
    const storageKey = `genius-profile-completion-email-sent-${user.id}`
    const hasSentEmail = localStorage.getItem(storageKey) === 'true'
    
    if (!hasSentEmail) {
      // Mark as sent immediately to prevent duplicate sends
      localStorage.setItem(storageKey, 'true')
      
      // Send completion email (non-blocking)
      emailService.sendGeniusProfileCompletedEmail(freelancerProfile.email).catch((error) => {
        console.error('Failed to send profile completion email:', error)
        // Remove the flag on error so we can retry
        localStorage.removeItem(storageKey)
      })
    }
  }, [profileCompletion.isComplete, freelancerProfile?.email, user?.id])

  // State to track live video progress
  const [liveProgress, setLiveProgress] = useState<number | null>(
    lesson?.progress?.percent_watched || null
  )

  // Always call useCourseNavigation, but disable it when we don't have course data yet
  const { navigation, isLoading: navLoading } = useCourseNavigation(
    lesson?.course_id || '',
    lessonId!
  )

  // Update live progress when lesson data changes
  useEffect(() => {
    if (lesson?.progress?.percent_watched !== undefined) {
      setLiveProgress(lesson.progress.percent_watched)
    }
  }, [lesson?.progress?.percent_watched])

  // Call all conditional hooks before early returns
  const handleProgressUpdate = useCallback((progressData: {
    seconds_watched: number;
    percent_watched: number;
    last_position_sec: number;
    duration: number;
  }) => {
    // Update live progress state for immediate UI feedback
    setLiveProgress(progressData.percent_watched * 100)
    updateProgress(progressData)
  }, [updateProgress])

  const handleImmediateProgressUpdate = useCallback((percent: number) => {
    setLiveProgress(percent)
  }, [])

  const handleLessonComplete = useCallback(() => {
    markComplete()
  }, [markComplete])

  // Memoize initialProgress to avoid recreating object on every render
  const initialProgress = useMemo(() => {
    if (!lesson?.progress) return undefined;

    return {
      seconds_watched: lesson.progress.seconds_watched || 0,
      percent_watched: (parseFloat(String(lesson.progress.percent_watched || 0))) / 100,
      last_position_sec: lesson.progress.last_position_sec || 0,
      furthest_watched_sec: lesson.progress.furthest_watched_sec || lesson.progress.last_position_sec || 0,
      completed_at: lesson.progress.completed_at,
    };
  }, [lesson?.progress])

  // Only show lessons page to geniuses
  if (!profile?.is_genius) {
    return <Navigate to="/genius/courses" replace />
  }

  // Show loading only if we have no cached data
  if (lessonLoading && !lesson) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <AnimatedLoader message="Loading lesson..." />
      </div>
    )
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-8 py-16">
          <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
            <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Lesson Not Found</h1>
            <p className="text-slate-500 mb-6">
              The lesson you're looking for doesn't exist or isn't available.
            </p>
            <Link
              to="/genius/courses"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-medium text-base"
              style={{
                background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Courses
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const isCompleted = Boolean(lesson.progress?.completed_at)
  const progressPercent = Math.min(Math.round(liveProgress || 0), 100)

  const getLessonIcon = (lessonType: string) => {
    switch (lessonType) {
      case 'video':
        return <PlayCircle className="h-5 w-5" />
      case 'reading':
        return <FileText className="h-5 w-5" />
      case 'quiz':
        return <HelpCircle className="h-5 w-5" />
      default:
        return <BookOpen className="h-5 w-5" />
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Hero Header */}
      <div className="max-w-[1400px] mx-auto px-8 pt-8">
        <div
          className="rounded-[32px] p-8 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)',
          }}
        >
          {/* Decorative circles */}
          <div
            className="absolute -right-[60px] -top-[60px] w-[200px] h-[200px] rounded-full"
            style={{
              background: 'radial-gradient(closest-side, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 100%)',
            }}
          />

          <div className="relative z-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-4">
              <Link to="/genius/courses" className="text-slate-400 hover:text-white transition-colors">
                Courses
              </Link>
              <span className="text-slate-500">/</span>
              <Link
                to={`/genius/course/${lesson.course.slug}`}
                className="text-slate-400 hover:text-white transition-colors"
              >
                {lesson.course.title}
              </Link>
              <span className="text-slate-500">/</span>
              <span className="text-white">{lesson.title}</span>
            </div>

            {/* Lesson Title & Info */}
            <div className="flex items-start justify-between gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 text-slate-300 capitalize">
                    {getLessonIcon(lesson.lesson_type)}
                    {lesson.lesson_type}
                  </span>
                  {isCompleted && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Completed
                    </span>
                  )}
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">{lesson.title}</h1>
                {lesson.description && (
                  <p className="text-slate-300 max-w-[700px]">{lesson.description}</p>
                )}
              </div>

              {/* Progress Stats */}
              <div className="flex items-center gap-6">
                {lesson.duration_seconds && (
                  <div className="text-center">
                    <div className="flex items-center gap-2 text-slate-300 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-xl font-bold text-white">
                        {Math.ceil(lesson.duration_seconds / 60)}m
                      </span>
                    </div>
                    <div className="text-xs text-slate-400">Duration</div>
                  </div>
                )}
                <div className="text-center">
                  <div className="text-xl font-bold text-white mb-1">{progressPercent}%</div>
                  <div className="text-xs text-slate-400">Progress</div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="bg-white/10 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    isCompleted ? 'bg-emerald-500' : 'bg-[#4669ed]'
                  }`}
                  style={{ width: `${Math.min(progressPercent, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-8 mt-6">
        <div className="flex gap-8">
          {/* Video Player Area */}
          <div className="flex-1 space-y-6">
            {/* Video Player Card */}
            <div className="bg-white rounded-[32px] border border-slate-200 overflow-hidden">
              {lesson.lesson_type === 'video' && lesson.vimeo_video_id ? (
                <VimeoPlayer
                  videoId={lesson.vimeo_video_id}
                  title={lesson.title}
                  initialProgress={initialProgress}
                  onProgressUpdate={handleProgressUpdate}
                  onImmediateProgressUpdate={handleImmediateProgressUpdate}
                  onLessonComplete={handleLessonComplete}
                />
              ) : (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-[rgba(70,105,237,0.10)] flex items-center justify-center mx-auto mb-4">
                    {getLessonIcon(lesson.lesson_type)}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {lesson.lesson_type === 'reading' ? 'Reading Material' : 'Quiz'}
                  </h3>
                  <p className="text-slate-500 mb-6">
                    {lesson.lesson_type === 'reading'
                      ? 'This lesson contains reading material. Review the content and mark as complete when done.'
                      : 'This lesson contains a quiz. Complete the quiz to mark this lesson as done.'}
                  </p>
                  {!isCompleted && (
                    <button
                      onClick={handleLessonComplete}
                      disabled={isUpdating}
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-medium text-base disabled:opacity-50"
                      style={{
                        background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                      }}
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      {isUpdating ? 'Marking Complete...' : 'Mark as Complete'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Navigation */}
            {!navLoading && navigation && (
              <div className="bg-white rounded-[32px] border border-slate-200 p-6">
                <div className="flex items-center justify-between">
                  {/* Previous */}
                  <div className="flex-1">
                    {navigation.previousLesson ? (
                      <Link
                        to={`/genius/lesson/${navigation.previousLesson.id}`}
                        className="inline-flex items-center gap-3 px-5 py-3 rounded-full border-2 border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5" />
                        <div className="text-left">
                          <div className="text-xs text-slate-400">Previous</div>
                          <div className="text-sm font-medium text-slate-900 line-clamp-1 max-w-[200px]">
                            {navigation.previousLesson.title}
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <div />
                    )}
                  </div>

                  {/* Position Indicator */}
                  <div className="px-6 text-center">
                    <span className="text-sm font-medium text-slate-900">
                      {navigation.currentIndex + 1}
                    </span>
                    <span className="text-slate-400 mx-1">/</span>
                    <span className="text-sm text-slate-500">
                      {navigation.totalLessons}
                    </span>
                  </div>

                  {/* Next */}
                  <div className="flex-1 flex justify-end">
                    {navigation.nextLesson ? (
                      <Link
                        to={`/genius/lesson/${navigation.nextLesson.id}`}
                        className="inline-flex items-center gap-3 px-5 py-3 rounded-full text-white font-medium"
                        style={{
                          background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                        }}
                      >
                        <div className="text-right">
                          <div className="text-xs text-white/70">Next</div>
                          <div className="text-sm font-medium line-clamp-1 max-w-[200px]">
                            {navigation.nextLesson.title}
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    ) : (
                      <Link
                        to={`/genius/course/${lesson.course.slug}`}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium"
                        style={{
                          background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                        }}
                      >
                        <CheckCircle2 className="w-5 h-5" />
                        Back to Course
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-[320px] flex-shrink-0 space-y-5">
            {/* Progress Card */}
            <div className="bg-white rounded-[32px] border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Lesson Progress</h3>

              <div className="space-y-4">
                {/* Progress Circle */}
                <div className="flex items-center gap-4">
                  <div className="relative w-16 h-16">
                    <svg className="w-16 h-16 transform -rotate-90">
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="#e2e8f0"
                        strokeWidth="6"
                        fill="none"
                      />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke={isCompleted ? '#10b981' : '#4669ed'}
                        strokeWidth="6"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - progressPercent / 100)}`}
                        strokeLinecap="round"
                        className="transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-bold text-slate-900">{progressPercent}%</span>
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">
                      {isCompleted ? 'Completed!' : 'In Progress'}
                    </div>
                    <div className="text-sm text-slate-500">
                      {isCompleted ? 'Great job!' : 'Keep watching to complete'}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Type</span>
                    <span className="font-medium text-slate-900 capitalize">{lesson.lesson_type}</span>
                  </div>
                  {lesson.duration_seconds && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Duration</span>
                      <span className="font-medium text-slate-900 flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                        {Math.ceil(lesson.duration_seconds / 60)} min
                      </span>
                    </div>
                  )}
                  {lesson.progress?.first_started_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Started</span>
                      <span className="font-medium text-slate-900">
                        {new Date(lesson.progress.first_started_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {lesson.progress?.completed_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Completed</span>
                      <span className="font-medium text-emerald-600">
                        {new Date(lesson.progress.completed_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Course Card */}
            <div className="bg-white rounded-[32px] border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-[16px] bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-[#4669ed]" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-400 uppercase tracking-wider">Course</div>
                  <div className="font-medium text-slate-900 line-clamp-1">{lesson.course.title}</div>
                </div>
              </div>

              {!navLoading && navigation && (
                <div className="text-sm text-slate-500 mb-4">
                  Lesson {navigation.currentIndex + 1} of {navigation.totalLessons}
                </div>
              )}

              <Link
                to={`/genius/course/${lesson.course.slug}`}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full border-2 border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                View All Lessons
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
